import {
    Bike,
    Bus,
    Calendar,
    Car,
    Clock,
    FileText,
    Gift,
    History,
    Layers,
    LayoutDashboard,
    Megaphone,
    MessageSquare,
    QrCode,
    Ship,
    Star,
    Store,
    TrendingUp,
    UserCog,
    Users,
    Wrench
} from "lucide-react";
import moment from 'moment';
import Switch from "rc-switch";
import 'rc-switch/assets/index.css';
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ReactSortable } from "react-sortablejs";
import ReactTable from "react-table";
import { Badge, Button, Card, CardBody, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";

import ListPageHeading from "../../containers/navs/ListPageHeading";
import { firestore } from "../../helpers/Firebase";
import { Capitalize, deviceWidth, getBannerName, getCollectionName } from "../../helpers/Utils";
import DataTablePagination from "../DatatablePagination";
import { Colxx } from "./CustomBootstrap";
import ReassignBadge from "./ReassignBadge";


// Component to handle Claimed count with hooks
const ClaimedCountComponent = ({ voucherKey }) => {
    const [claimedCount, setClaimedCount] = useState(0);

    useEffect(() => {
        if (!voucherKey) return;

        async function getClaimed() {
            try {
                let allDocsSnapshot = await firestore().collection('vouchers').doc(voucherKey).collection('claimed').get();
                let docsLength = allDocsSnapshot.docs.length;
                setClaimedCount(docsLength);
            } catch (error) {
                console.error('Error fetching claimed count:', error);
                setClaimedCount(0);
            }
        }
        getClaimed();
    }, [voucherKey]);

    return (
        <div className="list-item-heading">
            {claimedCount}
        </div>
    );
};

// Map collection names to appropriate icons for empty states
const getEmptyStateIcon = (selected) => {
    const iconMap = {
        'service_provider': Store,
        'providers': Store,
        'workers': UserCog,
        'washers': UserCog,
        'requests': Calendar,
        'appointments': Calendar,
        'chat': MessageSquare,
        'subscriptions': Clock,
        'orders': Clock,
        'services': Wrench,
        'marketing': Megaphone,
        'customers': Users,
        'users': Users,
        'promocodes': QrCode,
        'banners': Layers,
        'featuredProviders': Star,
        'vouchers': Gift,
        'compensations': Gift,
        'vehicles': Car,
        'cars': Car,
        'bikes': Bike,
        'caravans': Bus,
        'boats': Ship,
        'dashboard': LayoutDashboard,
        'orderHistory': History,
        'up selling': TrendingUp,
        'upselling': TrendingUp,
    };

    // Try to find exact match first
    if (iconMap[selected]) {
        return iconMap[selected];
    }

    // Try case-insensitive match
    const lowerSelected = selected?.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
        if (key.toLowerCase() === lowerSelected) {
            return icon;
        }
    }

    // Default to FileText if no match found
    return FileText;
};

const MainPage = ({
    title,
    showCreateButton = false,
    selected,
    data,
    setSelectedItem,
    tableColumns,
    permenatDelete = false,
    filterComponent = null,
    exportCSV = null,
    user = null,
    enableSorting = false,
    defaultSorting = "created",
    sortDirection = "asc",
    customDeleteFunction = null,
    customDeleteTitle = null,
    hideDefaultDeleteButton = false,
    // When true, delete-button click always opens the same confirmation modal
    // used by the default delete flow — even when a customDeleteFunction is
    // provided. Confirm still routes through the custom function; only the
    // "no dialog, direct action" shortcut is suppressed.
    confirmCustomDelete = false,
    customEditTitle = null,
    customEditFunction = null,
    customCreateFunction = null,
    customRowClickFunction = null,
    customSwitchFunction = null,
    customActionButtons = []
}) => {

    console.log('selected', selected)
    const { provider, services, advServices } = useSelector(state => state.providers) || {}

    const [deleteItem, setDeleteItem] = useState(false)
    const [sortItems, setSortItems] = useState(false)
    const [sortedItems, setSortedItems] = useState([])
    const [isUpdatingSorting, setIsUpdatingSorting] = useState(false)
    console.log('sortedItems', sortedItems)

    // Collect all action buttons
    const actionButtons = [];

    // Add custom action buttons first
    if (customActionButtons && customActionButtons.length > 0) {
        actionButtons.push(...customActionButtons);
    }

    // Add Delete button if not hidden
    if (!hideDefaultDeleteButton) {
        // When `confirmCustomDelete` is true, always go through the shared
        // confirmation modal — `deleteItemFunc` already routes the confirmed
        // action through `customDeleteFunction` when one is provided.
        const useConfirmDialog = confirmCustomDelete || !customDeleteFunction;
        actionButtons.push({
            accessor: "delete",
            func: useConfirmDialog ? (e) => setDeleteItem(e) : customDeleteFunction,
            color: "danger",
            title: customDeleteTitle || 'Delete',
            show: (user?.accessType === 'superAdmin') && deviceWidth() === 'desktop'
        });
    }

    // Add Edit button
    actionButtons.push({
        accessor: "edit",
        func: customEditFunction ? customEditFunction : (e) => {
            setSelectedItem(e || {})
        },
        color: "primary",
        title: customEditTitle || 'Edit',
        // show: deviceWidth() === 'desktop'
    });

    const dataColumns = [
        ...tableColumns,
        {
            Header: "",
            width: actionButtons.length * 120,
            accessor: "actions",
            type: 'actions',
            buttons: actionButtons
        },
    ]
    const dataTableColumns = () => {

        const handleSwitchChange = async (props, switchCheckedPrimary) => {
            const { id, key } = props.original;
            // Consumers can override the write path (e.g. marketing lists whose
            // Firestore collection name differs from the `selected` label).
            if (typeof customSwitchFunction === 'function') {
                return customSwitchFunction(props.original, switchCheckedPrimary);
            }
            const updateData = { active: switchCheckedPrimary, updated: Date.now() };
            return firestore().collection(selected).doc(id || key).update(updateData);
        };

        const renderCell = (rec, props) => {

            switch (rec.type) {
                case 'switch':
                    return (
                        <Switch
                            className="custom-switch custom-switch-primary"
                            checked={props.value}
                            onChange={switchCheckedPrimary => handleSwitchChange(props, switchCheckedPrimary)}
                        />

                    );
                case 'amount':
                    return (
                        <div className="list-item-heading" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            lineClamp: 2,
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            AED {Number(props.value).toFixed(2)}
                        </div>
                    );
                case 'img':
                    return (
                        <div className="providerfieldText">
                            <img
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '200px',
                                    border: '1px solid lightgrey',
                                    padding: '2px',
                                    objectFit: 'cover'
                                }}
                                src={props.value?.[0]}
                                alt={props.value?.[0]}
                            />
                        </div>
                    );
                case 'label':
                    // Check if there's a custom formatValue function
                    const formattedValue = rec.formatValue
                        ? rec.formatValue(props.value, props.original)
                        : (props.value?.en || props.value || '--');
                    return (
                        <div className="list-item-heading" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            lineClamp: 2,
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            {formattedValue}
                        </div>
                    );
                case 'customer':
                    return (
                        <div className="serviceWasherfieldText">
                            {props.value || '--'}
                            {rec.showCustomerNumber && props.original.customer_number && (
                                <><br />{props.original.customer_number}</>
                            )}
                        </div>
                    );
                case 'discount':
                    return (
                        <div className="list-item-heading" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            lineClamp: 2,
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            {props.value ? props.value + "%" : '--'}
                        </div>
                    );
                case 'currency':
                    return (
                        <div className="list-item-heading" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            lineClamp: 2,
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            {props.value ? props.value + " AED" : '--'}
                        </div>
                    );
                case 'provider':
                    return (
                        <div className="list-item-heading" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            lineClamp: 2,
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            {provider && provider[props.value] ? provider[props.value].name || '--' : props.value || '--'}
                        </div>
                    );
                case 'busyTimings':
                    return (
                        <p className="washerfieldText">
                            {props.value && moment().isBefore(moment(props.value?.endTime)) && <>{"From " + moment(props.value?.startTime).format('DD/MM/YYYY hh:mm A')}<br />
                                {"To " + moment(props.value?.endTime).format('DD/MM/YYYY hh:mm A')}</>}
                            <div
                                className="clickable-cell-element"
                                style={{ fontSize: '12px', color: '#3498db', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', fontWeight: '600', cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    rec.func(props.original, 'busyTimingsLogs');
                                }}
                            >
                                Show Logs
                            </div>
                            <div
                                className="clickable-cell-element"
                                style={{ fontSize: '12px', color: '#3498db', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', fontWeight: '600', cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    rec.func(props.original, 'busyTimingsManagement');
                                }}
                            >
                                Manage Busy Timings
                            </div>
                        </p>
                    );
                case 'modifier':
                    return (
                        <div className="list-item-heading" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            lineClamp: 2,
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            {props.value && typeof props.value === 'object' ?
                                Object.keys(props.value).map((modifierKey, index) => {
                                    const modifier = props.value[modifierKey];
                                    return (
                                        <div key={index} style={{ marginBottom: '4px' }}>
                                            {modifier?.name + " (Price: " + modifier.price + " AED, Duration: " + modifier.duration + ")"}
                                        </div>
                                    );
                                })
                                :
                                `${props.value || ''} AED` || 'No modifiers'
                            }
                        </div>
                    );

                case 'bannerName':
                    return (
                        <div className="list-item-heading" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            lineClamp: 2,
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            {getBannerName(props.original, { services, advServices, provider })}
                        </div>
                    );
                case 'date':
                    return (
                        <div className="list-item-heading" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            lineClamp: 2,
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}>
                            {props.value ? moment(props.value).format('DD/MM/YYYY hh:mm A') : '--'}
                        </div>
                    );
                case 'appointmentDate':
                    // Check if orderCreated column exists in table (only for requests.js)
                    const orderCreatedValue = selected === 'requests' ? props.original?.created : null;
                    const orderCreatedText = orderCreatedValue
                        ? (moment(orderCreatedValue).to(moment()).replace('in ', '')) + " Ago"
                        : null;

                    return (
                        <div className="datefieldText" style={{ position: 'relative' }}>
                            {orderCreatedText && (
                                <div className="order-created-indicator mb-2">
                                    <Badge color="primary" pill>
                                        {orderCreatedText}
                                    </Badge>
                                </div>
                            )}
                            {props.value ? (
                                <>
                                    {moment(props.value).format('DD/MM/YYYY')}
                                    <br />
                                    {moment(props.value).format('hh:mm A')}
                                </>
                            ) : '--'}

                        </div>
                    );
                case 'badge':
                    return (
                        <div className="list-item-heading" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start'
                        }}>
                            <Badge color={rec.color} pill>
                                {props.value}
                            </Badge>
                        </div>
                    );

                case 'orderType':
                    return (
                        <div className="list-item-heading" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start'
                        }}>
                            {props.value ? <Badge color={props.value === 'onlineBooking' ? 'success' : 'danger'} pill>{props.value === 'onlineBooking' ? 'Online' : 'Offline'}</Badge> : '--'}
                        </div>
                    );
                case 'orderStatus':
                    return (
                        <div className={(
                            props.original.status === 'pending' || props.original.status === 'new' ?
                                "pendingStatusfieldText"
                                : props.original.status === 'accepted' ?
                                    "acceptedStatusfieldText"

                                    : props.original.status === 'active' ?
                                        "processStatusfieldText"

                                        : props.original.status === 'completed' || props.original.status === 'notRated' ?
                                            "completedStatusfieldText"

                                            : props.original.status === 'rejected' ?
                                                "rejectedStatusfieldText"

                                                : props.original.status === 'rated' ?
                                                    "ratedStatusfieldText"

                                                    : props.original.status === 'canceled' ?
                                                        "rejectedStatusfieldText"
                                                        : ''
                        )
                        }>{props.value === 'new' ? 'Pending' : props.value === 'notRated' ? "Completed" : Capitalize(props.value)}
                            {props.original?.reassigning === true && <ReassignBadge reassignment={props.original?.reassignment} />}
                        </div>
                    );
                case 'button':
                    const buttonTitle = typeof rec.title === 'function'
                        ? rec.title(props.original)
                        : (rec.title || 'Edit');
                    return (
                        <div className="providerfieldText" style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}>
                            <Button
                                color={rec.color}
                                size="sm"
                                outline
                                className="top-right-button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    rec.func(props.original);
                                }}
                            >
                                {buttonTitle}
                            </Button>
                        </div>
                    );
                case 'actions':
                    // Render all action buttons in one row
                    const visibleButtons = rec.buttons?.filter(btn => {
                        if (typeof btn.show === 'function') {
                            return btn.show(props.original);
                        }
                        return btn.show !== false;
                    }) || [];

                    return (
                        <div className="table-actions-container" style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '0.5rem',
                            flexWrap: 'nowrap'
                        }}>
                            {visibleButtons.map((btn, index) => {
                                // Handle button title - can be string, function, or undefined
                                let buttonTitle = 'Action'; // default fallback

                                if (btn.title) {
                                    if (typeof btn.title === 'function') {
                                        buttonTitle = btn.title(props.original);
                                    } else if (typeof btn.title === 'string') {
                                        buttonTitle = btn.title;
                                    }
                                } else {
                                    // Fallback to accessor-based title if no title provided
                                    buttonTitle = btn.accessor === 'delete' ? 'Delete' :
                                        btn.accessor === 'edit' ? 'Edit' :
                                            'Action';
                                }

                                return (
                                    <Button
                                        key={`${btn.accessor}-${index}`}
                                        color={btn.color}
                                        size="sm"
                                        outline
                                        className="table-action-button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            btn.func(props.original);
                                        }}
                                    >
                                        {buttonTitle}
                                    </Button>
                                );
                            })}
                        </div>
                    );
                case 'claimed':
                    // This will be handled by a component that can use hooks
                    return <ClaimedCountComponent voucherKey={props.original?.key} />;
                default:
                    return null;
            }
        };

        // Find first visible column accessor for delay indicator placement
        // We'll check this inside the Cell render where props is available
        const firstVisibleColumnAccessor = dataColumns.find(col => {
            if (col.type === 'actions') return false;
            // For function-based show, we'll handle it in Cell render
            // Here we just check non-function show values
            if (typeof col.show === 'function') return true; // Assume visible, will be checked in Cell
            return col.show !== undefined ? col.show : !col.hide;
        })?.accessor;

        const isDesktop = deviceWidth() === 'desktop';
        return dataColumns?.map((rec, index) => {
            // For actions column, check if any button should be shown
            let shouldShow = rec.show !== undefined ? rec.show : !rec.hide;
            if (rec.type === 'actions' && rec.buttons) {
                // Show actions column if at least one button should be visible
                shouldShow = rec.buttons.some(btn => {
                    if (typeof btn.show === 'function') {
                        // Can't evaluate function here, so show column and filter in renderCell
                        return true;
                    }
                    return btn.show !== false;
                });
            }

            const columnDef = {
                Header: rec.Header,
                accessor: rec.accessor,
                id: rec.accessor,
                width: rec.width,
                // A column may ask for a larger flex basis (react-table grows width-less columns from minWidth).
                minWidth: Number(rec.minWidth) > 50 ? Number(rec.minWidth) : 50,
                resizable: isDesktop && rec.type !== 'actions',
                show: shouldShow,
                getHeaderProps: () => ({
                    title: rec.Header || '' // Simple native tooltip
                }),
                Cell: props => {
                    const isDelayed = props.original?.delayed;
                    const delayText = props.original?.delayText;

                    // Check if this is actually the first visible column (handle function-based show)
                    let isActuallyFirstVisible = false;
                    if (firstVisibleColumnAccessor && rec.accessor === firstVisibleColumnAccessor) {
                        // If show is a function, evaluate it with props
                        if (typeof rec.show === 'function') {
                            isActuallyFirstVisible = rec.show(props.original);
                        } else {
                            isActuallyFirstVisible = rec.show !== undefined ? rec.show : !rec.hide;
                        }
                    }

                    // Check if column has a custom Cell renderer
                    if (rec.Cell && typeof rec.Cell === 'function') {
                        return (
                            <div className="list-item-heading" style={{ width: '100%' }}>
                                {isActuallyFirstVisible && isDelayed && delayText && (
                                    <div className="delay-indicator mb-2" data-delay-text={delayText}>
                                        <Badge color="danger" pill>
                                            {delayText}
                                        </Badge>
                                    </div>
                                )}
                                {rec.Cell(props)}
                            </div>
                        );
                    }

                    return (
                        <div className="list-item-heading" style={{ width: '100%' }}>
                            {isActuallyFirstVisible && isDelayed && delayText && (
                                <div className="delay-indicator mb-2" data-delay-text={delayText}>
                                    <Badge color="danger" pill>
                                        {delayText}
                                    </Badge>
                                </div>
                            )}
                            {renderCell(rec, props)}

                        </div>
                    );
                },
            };
            return columnDef;
        });
    };

    const deleteItemFunc = (item) => {
        if (customDeleteFunction) {
            customDeleteFunction(item)
            setDeleteItem(false)
            return
        }
        if (permenatDelete) {
            firestore().collection(selected).doc(item.id || item.key).delete().then(() => {
                setDeleteItem(false)
            })
        } else {
            firestore().collection(selected).doc(item.id || item.key).update({ deActivated: true, updated: Date.now(), active: false }).then(() => {
                setDeleteItem(false)
            })
        }
    }

    // Handle sorting functionality
    const handleSort = () => {
        const activeItems = data?.filter(item => item.active)?.sort((a, b) => (a.index || 0) - (b.index || 0));
        setSortedItems(activeItems || []);
        setSortItems(true);
    };

    // Handle export functionality
    const handleExport = () => {
        if (exportCSV) {
            exportCSV(data);
        }
    };

    const updateSorting = async () => {
        if (isUpdatingSorting || !sortedItems.length) return;

        try {
            setIsUpdatingSorting(true);

            // Use Firestore batch for better performance and atomicity
            const timestamp = Date.now();

            // Process items in chunks to avoid hitting Firestore's 500 operation limit per batch
            const BATCH_SIZE = 500;
            const chunks = [];

            for (let i = 0; i < sortedItems.length; i += BATCH_SIZE) {
                chunks.push(sortedItems.slice(i, i + BATCH_SIZE));
            }

            // Process each chunk
            for (const chunk of chunks) {
                const currentBatch = firestore().batch();

                chunk.forEach((item, chunkIndex) => {
                    const globalIndex = chunks.indexOf(chunk) * BATCH_SIZE + chunkIndex;
                    const docId = item.id || item.key;
                    const selectedCollection = getCollectionName(item);

                    // Prepare update data based on collection type
                    let updateData;
                    if (selected === 'service_provider') {
                        updateData = {
                            featured: { ...item.featured, index: globalIndex },
                            updated: timestamp
                        };
                    } else {
                        updateData = { index: globalIndex, updated: timestamp };
                    }

                    const docRef = firestore()
                        .collection(selected === 'services' ? selectedCollection : selected)
                        .doc(docId);

                    currentBatch.update(docRef, updateData);
                });

                // Commit the current batch
                await currentBatch.commit();
            }

            setSortItems(false);
            console.log(`Successfully updated sorting for ${sortedItems.length} items`);

        } catch (error) {
            console.error('Error updating sort order:', error);
            // You could add a toast notification here for better UX
            alert('Failed to update sorting. Please try again.');
        } finally {
            setIsUpdatingSorting(false);
        }
    };

    // Note: Filtering should be done in the parent component before passing data to MainPage
    // This keeps each component responsible for its own filtering logic


    // Calculate page size based on available data length
    // This ensures no empty rows are rendered
    const calculatePageSize = () => {
        if (!data || !data.length) return 10; // Default if no data

        const dataLength = data.length;
        const maxPageSize = deviceWidth() === 'mobile' ? 20 : deviceWidth() === 'tablet' ? 30 : 50;

        // If data is less than max, use exact data length to avoid empty rows
        // If data is more than max, use max for pagination
        return dataLength <= maxPageSize ? dataLength : maxPageSize;
    };

    const dynamicPageSize = calculatePageSize();

    // Show loading spinner only when data is null or undefined (not yet loaded)
    // Show content (table or "no data" message) when data is an array (even if empty)
    if (data === null || data === undefined) {
        return <div className="loading" />;
    }

    return (
        <Fragment >
            <div className={"disable-text-selection"}>
                <ListPageHeading
                    accessType={user?.accessType}
                    heading={title}
                    showCreateButton={showCreateButton}
                    showSearch={true}
                    toggleModal={() => {
                        customCreateFunction ? customCreateFunction() : setSelectedItem({ addNew: true })
                    }}
                    filterComponent={filterComponent}
                    sortBanners={enableSorting ? handleSort : undefined}
                    exportCSV={exportCSV ? handleExport : undefined}
                />

                <Row className="survey-app ">
                    <Colxx xxs="12 pb-5">
                        {
                            data && data.length > 0 ?
                                <div style={{ overflowX: 'auto', width: '100%' }} className="react-table-scroll-wrapper">
                                    <ReactTable
                                        data={data}
                                        columns={dataTableColumns()}
                                        resizable={deviceWidth() === 'desktop'}
                                        defaultPageSize={dynamicPageSize}
                                        pageSize={dynamicPageSize}
                                        minRows={0}
                                        showPageJump={false}
                                        showPageSizeOptions={false}
                                        PaginationComponent={DataTablePagination}
                                        className='custuomReactTable'
                                        defaultSorted={[
                                            {
                                                id: defaultSorting || "created",
                                                desc: sortDirection === 'asc' ? false : true
                                            }
                                        ]}
                                        getTrGroupProps={(state, rowInfo, column, instance) => {
                                            const isDelayed = rowInfo?.original?.delayed;
                                            const delayText = rowInfo?.original?.delayText;
                                            const isQuickWash = rowInfo?.original?.service_type === 'quickWash' || (rowInfo?.original?.service_type === 'recovery' && rowInfo?.original?.recoveryInfo?.scheduleType === 'sos');

                                            // Build className array for multiple conditions
                                            const classNameArray = [];
                                            if (isDelayed) classNameArray.push('row-delayed');
                                            if (isQuickWash) classNameArray.push('row-quickwash');

                                            return {
                                                onClick: (e) => {
                                                    // Only trigger if clicking on the row, not on interactive elements
                                                    const target = e.target;

                                                    // Check if clicked element or its parent is interactive
                                                    const isInteractive =
                                                        target.tagName === 'BUTTON' ||
                                                        target.closest('button') ||
                                                        target.tagName === 'A' ||
                                                        target.closest('a') ||
                                                        target.closest('.delay-indicator') ||
                                                        target.closest('.clickable-cell-element') ||
                                                        target.closest('[data-clickable="true"]');

                                                    if (!isInteractive) {
                                                        if (customRowClickFunction) {
                                                            customRowClickFunction(rowInfo.original);
                                                        } else if (customEditFunction) {
                                                            customEditFunction(rowInfo.original);
                                                        } else {
                                                            setSelectedItem(rowInfo.original || {});
                                                        }
                                                    }
                                                },
                                                className: classNameArray.join(' '),
                                                style: {
                                                    cursor: 'pointer'
                                                },
                                                'data-delayed': isDelayed ? 'true' : undefined,
                                                'data-delay-text': delayText || undefined,
                                                'data-quickwash': isQuickWash ? 'true' : undefined
                                            }
                                        }}
                                    />
                                </div>
                                :
                                <Card className="empty-table-card">
                                    <CardBody className="empty-table-state">
                                        <div className="empty-table-content">
                                            <div className="empty-table-icon">
                                                {React.createElement(getEmptyStateIcon(selected), { size: 40, strokeWidth: 1.5 })}
                                            </div>
                                            <h3 className="empty-table-title">No {title}</h3>
                                            <p className="empty-table-description">There are no {title.toLowerCase()} available at the moment.</p>
                                        </div>
                                    </CardBody>
                                </Card>
                        }
                    </Colxx>
                </Row>

                <Modal backdrop="static" isOpen={deleteItem}>
                    <ModalHeader >
                        {customDeleteTitle ? customDeleteTitle : 'Delete'} {deleteItem?.name?.en || deleteItem?.name}
                    </ModalHeader>
                    <ModalBody>
                        <Label className="mb-0">Are you sure you want to {customDeleteTitle ? customDeleteTitle : 'delete'} {deleteItem?.name?.en || deleteItem?.name}?</Label>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={() => {
                            setDeleteItem(false)
                        }}>Dismiss</Button>
                        <Button color='danger' onClick={() => {
                            deleteItemFunc(deleteItem)
                        }}>{customDeleteTitle ? customDeleteTitle : 'Delete'}</Button>
                    </ModalFooter>

                </Modal>

                {/* Sorting Modal */}
                {sortItems && (
                    <Modal backdrop="static" isOpen={sortItems}>
                        <ModalHeader>
                            Sort {title}
                        </ModalHeader>
                        <ModalBody>
                            <Row>
                                <Colxx xxs="12" className="mb-4">
                                    <ReactSortable
                                        list={sortedItems}
                                        setList={(newState) => {
                                            setSortedItems(newState);
                                        }}
                                        tag="ul"
                                        className="list-unstyled mt-2"
                                        options={{
                                            handle: ".handle"
                                        }}>
                                        {sortedItems?.map(item => {
                                            return (
                                                <li key={item.key || item.id} id={item.key || item.id} data-id={item.key || item.id} className='handle'>
                                                    <Card className={'mb-2'}>
                                                        <Row className='p-3'>
                                                            <Colxx xxs="12" className='d-flex align-items-center'>
                                                                <span className="badge badge-pill w-5 d-flex align-items-center">
                                                                    <i className='simple-icon-cursor-move' />
                                                                </span>
                                                                {item?.img?.[0] && (
                                                                    <img
                                                                        style={{
                                                                            width: '60px',
                                                                            height: '60px',
                                                                            borderRadius: '200px',
                                                                            border: '1px solid lightgrey',
                                                                            padding: '2px',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                        src={item?.img?.[0]}
                                                                        alt={''}
                                                                    />
                                                                )}
                                                                <div className='ml-2' style={{
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {selected === 'banners' ? getBannerName(item, { services, advServices, provider }) : (item?.name?.en || item?.name)}
                                                                </div>
                                                            </Colxx>
                                                        </Row>
                                                    </Card>
                                                </li>
                                            );
                                        })}
                                    </ReactSortable>
                                </Colxx>
                            </Row>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                onClick={() => setSortItems(false)}
                                disabled={isUpdatingSorting}
                            >
                                Dismiss
                            </Button>
                            <Button
                                color='primary'
                                onClick={updateSorting}
                                disabled={isUpdatingSorting}
                            >
                                {isUpdatingSorting ? 'Updating...' : 'Update Sorting'}
                            </Button>
                        </ModalFooter>
                    </Modal>
                )}
            </div>

        </Fragment>);
};

export default MainPage;